import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingPage } from "@/components/MarketingPage";
import { buildSocialMeta, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: buildSocialMeta({
      title: "Privacy Policy — CoachQuick",
      description: "How CoachQuick collects, uses, and protects coach and client data.",
      path: "/privacy",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/privacy` }],
  }),
  component: PrivacyPage,
});


function PrivacyPage() {
  return (
    <MarketingPage>
      <article className="space-y-6">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: May 8, 2026</p>
        </header>

        <section className="space-y-3">
          <p>
            CoachQuick ("we", "us") provides a CRM for independent sports coaches. This policy explains what
            information we collect, how we use it, and the choices you have. If you have questions, email{" "}
            our <Link to="/contact" className="underline">contact form</Link>.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Information we collect</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li><strong>Account info</strong> you provide when signing up: name, email, password hash, profile photo.</li>
            <li><strong>Client records</strong> you enter about your students: name, contact info, session notes, drills, homework, photos and video clips you upload.</li>
            <li><strong>Google Calendar data</strong> when you connect your Google account: OAuth tokens and read-only access to upcoming event metadata used to surface upcoming sessions.</li>
            <li><strong>Billing data</strong> processed by Stripe when you subscribe (we do not store full card numbers).</li>
            <li><strong>Usage and device data</strong>: IP address, browser, pages visited, and basic event logs to operate the service.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">How we use information</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>To provide and improve CoachQuick features (session logging, automated recaps, scheduling, invoicing).</li>
            <li>To send transactional email (account, billing, lesson recaps, re-engagement nudges you trigger).</li>
            <li>To prevent abuse and secure the service.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Service providers</h2>
          <p>We share data only with vendors that help us run CoachQuick:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li><strong>Lovable Cloud</strong> — hosting, database, authentication, file storage.</li>
            <li><strong>Stripe</strong> — billing and payments.</li>
            <li><strong>Google</strong> — calendar integration (only when you connect it).</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>OpenAI / Google AI</strong> — generating session recap drafts from notes you submit.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Data retention</h2>
          <p>We keep your account and client records for as long as your account is active. You can delete clients
            and sessions at any time from the app. Closing your account removes your data within 30 days, except
            where retention is required by law (e.g. tax records for paid invoices).</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Your rights</h2>
          <p>You can access, export, correct, or delete your data at any time. For requests, use our{" "}
            <Link to="/contact" className="underline">contact form</Link>.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Children</h2>
          <p>CoachQuick is intended for use by coaches (adults). Coaches may enter records about minor students;
            those records are processed on the coach's behalf.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Changes</h2>
          <p>We'll update this page when our practices change and revise the "Last updated" date above.</p>
        </section>
      </article>
    </MarketingPage>
  );
}
